// ------------------------------------------------------------
// Shared gate open/closed visual styling (panel + canvas)
// ------------------------------------------------------------

export type GateRuntimeState = 'open' | 'closed';
export type GateUiState = 'OPEN' | 'CLOSED' | 'OPENING' | 'CLOSING' | 'ERROR' | string;

export interface GateCanvasVisual {
  nodeFill: string;
  nodeStroke: string;
  haloFill: string;
  ringStroke: string;
  badgeFill: string;
  badgeText: string;
  badgeLabel: string;
}

export interface GateNodeMetrics {
  haloRadius: number;
  haloStrokeWidth: number;
  highlightRadius: number;
  highlightStrokeWidth: number;
  highlightShadowBlur: number;
  badgeWidth: number;
  badgeHeight: number;
  badgeCornerRadius: number;
  badgeFontSize: number;
  badgeOffsetY: number;
  nodeStrokeWidth: number;
  nodeShadowBlur: number;
}

/** Gate chrome sized proportionally to node radius (scales with counter-zoom groups). */
export function gateNodeMetrics(nodeRadius: number): GateNodeMetrics {
  const r = Math.max(6, nodeRadius);

  return {
    haloRadius: r * 1.1,
    haloStrokeWidth: Math.max(1.25, r * 0.11),
    highlightRadius: r * 1.18,
    highlightStrokeWidth: Math.max(1.5, r * 0.13),
    highlightShadowBlur: Math.max(2, r * 0.2),
    badgeWidth: r * 2.1,
    badgeHeight: r * 0.62,
    badgeCornerRadius: r * 0.28,
    badgeFontSize: Math.max(7, r * 0.34),
    badgeOffsetY: -(r * 1.22),
    nodeStrokeWidth: Math.max(1.5, r * 0.13),
    nodeShadowBlur: Math.max(2, r * 0.22),
  };
}

export function resolveGateCanvasVisual(
  state: GateRuntimeState,
  uiState?: GateUiState,
): GateCanvasVisual {
  if (uiState === 'ERROR') {
    return {
      nodeFill: '#b45309',
      nodeStroke: '#fcd34d',
      haloFill: 'rgba(245, 158, 11, 0.18)',
      ringStroke: '#fbbf24',
      badgeFill: '#92400e',
      badgeText: '#fef3c7',
      badgeLabel: 'ERROR',
    };
  }
  if (uiState === 'OPENING' || uiState === 'CLOSING') {
    return {
      nodeFill: '#d97706',
      nodeStroke: '#fde68a',
      haloFill: 'rgba(251, 191, 36, 0.18)',
      ringStroke: '#fbbf24',
      badgeFill: '#78350f',
      badgeText: '#fef3c7',
      badgeLabel: uiState,
    };
  }
  if (state === 'open') {
    return {
      nodeFill: '#16a34a',
      nodeStroke: '#bbf7d0',
      haloFill: 'rgba(34, 197, 94, 0.2)',
      ringStroke: '#4ade80',
      badgeFill: '#14532d',
      badgeText: '#dcfce7',
      badgeLabel: 'OPEN',
    };
  }
  return {
    nodeFill: '#dc2626',
    nodeStroke: '#fecaca',
    haloFill: 'rgba(239, 68, 68, 0.2)',
    ringStroke: '#f87171',
    badgeFill: '#7f1d1d',
    badgeText: '#fee2e2',
    badgeLabel: 'CLOSED',
  };
}

export function gatePanelSurfaceClass(
  state: GateRuntimeState,
  pending: boolean,
  highlighted: boolean,
): string {
  if (highlighted) {
    return 'border-sky-400/80 bg-sky-950/40 ring-2 ring-sky-400/50';
  }
  if (pending) {
    return 'border-amber-400/40 bg-amber-950/20';
  }
  if (state === 'open') {
    return 'border-emerald-500/35 bg-emerald-950/15';
  }
  return 'border-red-500/35 bg-red-950/15';
}

export function gatePanelRowClass(
  state: GateRuntimeState,
  pending: boolean,
  highlighted: boolean,
): string {
  const base =
    'flex flex-wrap items-center justify-between gap-3 rounded-lg border px-3 py-2 transition-colors';
  return `${base} ${gatePanelSurfaceClass(state, pending, highlighted)}`;
}

export function gatePanelCardClass(
  state: GateRuntimeState,
  pending: boolean,
  highlighted: boolean,
): string {
  const base =
    'flex min-w-0 flex-col gap-1 rounded-md border px-2 py-1.5 transition-colors';
  return `${base} ${gatePanelSurfaceClass(state, pending, highlighted)}`;
}
